import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  PostValue,
  PostValueLabel,
  KolAvatar,
  PostList,
  PostListItem,
  PostListItemBorderBottom,
  KolName,
  PostInfo,
  PostImage,
  PostContent,
  PostRank,
  SlideContainer,
  SlideTitle,
  Card,
  CardTitle,
} from '@/utils/export-pptx/components/slides/styles'
import { PptxObjectType } from '@/utils/export-pptx/constants'
import sliceTextByDisplayLength from '@/utils/export-pptx/slice-text-by-display-length'
import type { KolEngagementViewSlide } from '@/utils/export-pptx/types/slide'
import { getThumbnail } from '@/utils/get-thumbnails'

const MAX_POST_TITLE_LENGTH = 44

interface SlideProps {
  slide: KolEngagementViewSlide
  onSlideReady?: (slide: KolEngagementViewSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady?.(slide)
  })

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Shape}>
        <PlatformImage
          height={54}
          platform={slide.payload.platform}
          width={54}
        />
        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({
            id: 'insight_report:slide_kol_engagement_view_content_top5',
          })}
        </span>
      </SlideTitle>

      <GridContent>
        {slide.payload.engagementCountTop5 && (
          <Card data-pptx-object={PptxObjectType.Shape}>
            <CardTitle data-pptx-object={PptxObjectType.Text} mb={5}>
              {slide.payload.engagementCountTop5.title}
            </CardTitle>

            <PostList>
              <PostValueLabel data-pptx-object={PptxObjectType.Text}>
                {slide.payload.engagementCountTop5.label}
              </PostValueLabel>

              {slide.payload.engagementCountTop5.items.map((item, index) => (
                <PostListItem key={item.kol.uuid}>
                  <PostRank data-pptx-object={PptxObjectType.Text}>
                    #{index + 1}
                  </PostRank>
                  <PostImage
                    alt={`post-img-${index}`}
                    data-pptx-object={PptxObjectType.Image}
                    src={item.post.thumbnail}
                  />
                  <PostInfo>
                    <PostContent data-pptx-object={PptxObjectType.Text}>
                      {sliceTextByDisplayLength(
                        item.post.title,
                        MAX_POST_TITLE_LENGTH,
                      )}
                    </PostContent>
                    <div>
                      <KolAvatar
                        alt={`kol-avatar-${index}`}
                        data-pptx-object={PptxObjectType.Image}
                        src={getThumbnail(item.kol.uuid)}
                      />

                      <KolName data-pptx-object={PptxObjectType.Text}>
                        {item.kol.name}
                      </KolName>
                    </div>
                  </PostInfo>

                  <PostValue data-pptx-object={PptxObjectType.Text}>
                    {item.value}
                  </PostValue>

                  {index !==
                    slide.payload.engagementCountTop5!.items.length - 1 && (
                    <PostListItemBorderBottom
                      data-pptx-object={PptxObjectType.Shape}
                    />
                  )}
                </PostListItem>
              ))}
            </PostList>
          </Card>
        )}

        {slide.payload.viewCountTop5 && (
          <Card data-pptx-object={PptxObjectType.Shape}>
            <CardTitle data-pptx-object={PptxObjectType.Text} mb={5}>
              {slide.payload.viewCountTop5.title}
            </CardTitle>

            <PostList>
              <PostValueLabel data-pptx-object={PptxObjectType.Text}>
                {slide.payload.viewCountTop5.label}
              </PostValueLabel>

              {slide.payload.viewCountTop5.items.map((item, index) => (
                <PostListItem key={item.kol.uuid}>
                  <PostRank data-pptx-object={PptxObjectType.Text}>
                    #{index + 1}
                  </PostRank>
                  <PostImage
                    alt={`post-img-${index}`}
                    data-pptx-object={PptxObjectType.Image}
                    src={item.post.thumbnail}
                  />
                  <PostInfo>
                    <PostContent data-pptx-object={PptxObjectType.Text}>
                      {sliceTextByDisplayLength(
                        item.post.title,
                        MAX_POST_TITLE_LENGTH,
                      )}
                    </PostContent>
                    <div>
                      <KolAvatar
                        alt={`kol-avatar-${index}`}
                        data-pptx-object={PptxObjectType.Image}
                        src={getThumbnail(item.kol.uuid)}
                      />

                      <KolName data-pptx-object={PptxObjectType.Text}>
                        {item.kol.name}
                      </KolName>
                    </div>
                  </PostInfo>

                  <PostValue data-pptx-object={PptxObjectType.Text}>
                    {item.value}
                  </PostValue>

                  {index !== slide.payload.viewCountTop5!.items.length - 1 && (
                    <PostListItemBorderBottom
                      data-pptx-object={PptxObjectType.Shape}
                    />
                  )}
                </PostListItem>
              ))}
            </PostList>
          </Card>
        )}
      </GridContent>
    </SlideContainer>
  )
}

const GridContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;

  width: 100%;
`

export default SlideComponent
